import { useMemo, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useHotkeys } from 'react-hotkeys-hook'
import { lib } from '@gocanvas/client'
import {
  createElement,
  updateElement,
  createTable,
  moveTable,
  resizeTable,
  deleteTable,
} from 'apps/studio/store/actions/sharedActions'
import {
  createTableData,
  STATIC_TABLE_WIDTH_RATIO,
  STATIC_TABLE_TITLE_HEIGHT,
  STATIC_TABLE_ROW_HEIGHT,
  STATIC_TABLE_HEADER_ROWS,
  STATIC_TABLE_DATA_ROWS,
} from 'apps/studio/lib/utils/entities'
import {
  activeModeSelector,
  aiProgressSelector,
} from 'apps/studio/store/selectors/studioSettings'
import {
  globalPositionSelector,
  globalStylesSelector,
  elementsSelector,
  visibleStyledElementsWorkspaceSelector,
  activePageSelector,
  layoutsSelector,
} from 'apps/studio/store/selectors/pdf'
import {
  activeEntryGuidSelector,
} from 'apps/formsCreationKit/store/selectors/settings'
import Header from 'apps/studio/components/header'
import { Alert } from 'apps/studio/components/alert'
import { PDFViewer as PDFWorkspace } from 'apps/studio/components/pdfViewer'
import { Intro } from 'apps/studio/components/intro'
import { Test } from 'apps/studio/components/test'
import { LeftPanels } from 'apps/studio/components/leftPanel'
import { RightPanels } from 'apps/studio/components/rightPanel'
import { ElementOverlay } from 'apps/studio/components/dragAndDrop/ElementOverlay'
import { applyResizingStyles } from 'apps/studio/components/pdfViewer/elements/ResizeHandle'
import {
  calculatePagePosition,
  calculateResizePosition,
  calculateGuidelinePosition,
  handleResizeHandleDrop,
  handleTableResizeDrop,
  handleTableDividerResizeDrop,
  handleDataInputDrop,
  handlePdfElementDrop,
  handleTableMoveDrop,
  selectActiveEntryLabel,
} from 'apps/studio/lib/utils/dnd'
import { AIFormLoader } from 'apps/studio/components/workspace/AIFormLoader'
import {
  PublishModal,
  PurchaseModal,
} from 'apps/studio/components/modals'
import {
  activeModalNameSelector,
  saveAndPublishLoadedSelector,
} from 'apps/formsCreationKit/store/selectors/settings'
import { sheetsSelector, activeSheetSelector } from 'apps/formsCreationKit/store/selectors/entities'
import { appSettingsActions } from 'apps/studio/store/reducers/settings/appSettings'
const { resetActiveEntryAndSheet } = appSettingsActions
const { LOOP_SHEET_TYPE_ID } = lib
import {
  INTRO_MODE,
  PDF_MODE,
  TEST_MODE,
} from 'apps/studio/store/reducers/settings/studioSettings'
import { TransformWrapperProvider } from 'apps/studio/contexts/TransformWrapperContext'

const StudioControls = () => {
  return (
    <>
      <LeftPanels />
      <div className="workspace-spacing" />
      <RightPanels />
    </>
  )
}

const Workspaces = {
  [INTRO_MODE]: Intro,
  [PDF_MODE]: PDFWorkspace,
  [TEST_MODE]: Test,
}

export const StudioLayout = () => {
  const dispatch = useDispatch()
  const activeMode = useSelector(activeModeSelector)
  const globalPositionDefault = useSelector(globalPositionSelector)
  const globalStylesDefaults = useSelector(globalStylesSelector)
  const allElements = useSelector(elementsSelector)
  const allSheets = useSelector(sheetsSelector)
  const activeSheet = useSelector(activeSheetSelector)
  const activePage = useSelector(activePageSelector)
  const allLayouts = useSelector(layoutsSelector)
  const activeEntryGuid = useSelector(activeEntryGuidSelector)
  const visibleElements = useSelector(visibleStyledElementsWorkspaceSelector)

  useHotkeys(
    'delete, backspace',
    () => {
      if (!activeSheet || activeEntryGuid) return
      if (activeSheet.sheet_type_id !== LOOP_SHEET_TYPE_ID || !activeSheet.multi_section) return

      const topLevelTableSectionId = activeSheet.section_id
      const innerSectionId = activeSheet.multi_section
      const tableSheet = allSheets.find(s => s.section_id === innerSectionId)
      const layout = allLayouts.find(
        l =>
          l.section_id === topLevelTableSectionId && l.layout_type === 'table'
      )
      if (!layout || !activePage) return
      dispatch(
        deleteTable({
          layout,
          outerSection: { id: topLevelTableSectionId },
          innerSection: { id: innerSectionId },
          keyFieldSheet: activeSheet,
          tableSheet,
          page: { id: activePage.id },
        })
      )
    },
    [activeSheet, activeEntryGuid, allSheets, allLayouts, activePage]
  )
  const ActiveWorkspace = useMemo(() => Workspaces[activeMode], [activeMode])
  const [activeDraggable, setActiveDraggable] = useState(null)
  const [scaleFactor, setScaleFactor] = useState(1)
  const [showDropAnimation, setShowDropAnimation] = useState(false)
  const { active: aiLoadingActive } = useSelector(aiProgressSelector)
  const usersAndGroupsLoaded = useSelector(saveAndPublishLoadedSelector)
  const activeModalName = useSelector(activeModalNameSelector)
  const dragOverlayRef = useRef(null)
  const animationFrameId = useRef(null)
  const transformWrapperRef = useRef(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Sets the drag activation to start once the user moves the draggable 5px.
      // This prevents accidental drags when clicking on an element.
      activationConstraint: {
        distance: 5,
      },
    })
  )

  const handleDragStart = ({ active }) => {
    setActiveDraggable(active)
    setShowDropAnimation(false)
  }

  const handleDragMove = ({ active, over, delta }) => {
    if (over) {
      const { scaleFactor: droppableScaleFactor = 1 } = over.data.current
      if (droppableScaleFactor != scaleFactor) {
        setScaleFactor(droppableScaleFactor)
      }

      const { type } = active.data.current
      if (type === 'resizeHandle') {
        const { position, handleLocation, wrapperId } = active.data.current
        const elementWrapper = document.getElementById(wrapperId)
        const newPosition = calculateResizePosition({
          delta,
          dropTarget: over,
          originalPosition: position,
          handleLocation,
          scale: droppableScaleFactor,
        })

        applyResizingStyles(elementWrapper, newPosition)
      } else if (['dataInput', 'pageElement'].includes(type)) {
        const { element: draggedElement = {} } = active.data.current
        const {
          pageDefaultPosition,
          pageNumber,
          setGuidelineX,
          setGuidelineY,
        } = over.data.current

        const { left: draggableX, top: draggableY } =
          dragOverlayRef.current.getBoundingClientRect()
        const pageElements = visibleElements.filter(
          ({ page_number, entry_guid, position }) =>
            page_number === pageNumber &&
            entry_guid !== draggedElement.entry_guid &&
            position
        )

        const { x, y, width, height } = calculatePagePosition({
          draggablePosition: {
            ...(type === 'dataInput'
              ? pageDefaultPosition
              : draggedElement.position),
            x: draggableX,
            y: draggableY,
          },
          droppablePosition: over.rect,
          scaleFactor: over.data.current.scaleFactor,
        })
        const x2 = Math.round(x + width)
        const y2 = Math.round(y + height)

        const guidelinePosition = calculateGuidelinePosition(
          { x, y, x2, y2 },
          pageElements
        )

      const guidelineAnimations = []

      if (guidelinePosition.xAlign !== null) {
      guidelineAnimations.push(() =>
        setGuidelineX(Math.round(guidelinePosition.xAlign * scaleFactor))
      )
    } else if (guidelinePosition.xNear !== null) {
      guidelineAnimations.push(() =>
        setGuidelineX(Math.round(guidelinePosition.xNear * scaleFactor))
      )
    } else {
      guidelineAnimations.push(() => setGuidelineX(null))
    }

    if (guidelinePosition.yAlign !== null) {
      guidelineAnimations.push(() =>
        setGuidelineY(Math.round(guidelinePosition.yAlign * scaleFactor))
      )
    } else if (guidelinePosition.yNear !== null) {
      guidelineAnimations.push(() =>
        setGuidelineY(Math.round(guidelinePosition.yNear * scaleFactor))
      )
      } else {
        guidelineAnimations.push(() => setGuidelineY(null))
      }

      // cancel previous update if it hasn't run yet
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current)
      }

      // Apply all animations in one frame
      animationFrameId.current = requestAnimationFrame(() =>
        guidelineAnimations.forEach(fn => fn())
      )
      }
    }
  }

  const handleDragEnd = ({ active, over, delta }) => {
    setActiveDraggable(null)
    const { accepts = [] } = over?.data?.current || {}
    const { type } = active?.data?.current || {}

    if (over && accepts.includes(type)) {
      if (type === 'resizeHandle') {
        const { targetType } = active.data.current
        if (targetType === 'table') {
          dispatch(resizeTable(handleTableResizeDrop(active, over, delta)))
        } else {
          dispatch(updateElement(handleResizeHandleDrop(active, over, delta)))
        }
      } else if (type === 'tableDividerResize') {
        dispatch(resizeTable(handleTableDividerResizeDrop({ active, over, delta })))
      } else if (type === 'tableMove') {
        const payload = handleTableMoveDrop({
          active,
          over,
          delta,
          allSheets,
        })
        dispatch(moveTable(payload))
      } else if (type === 'table') {
        const { entryType } = active.data.current
        const { page, layout: rootLayout, scaleFactor: droppableScaleFactor = 1 } =
          over.data.current

        if (!entryType || !page || !rootLayout) return

        const activeRect = active.rect.current.translated
        if (!activeRect) return

        const pageWidth = over.rect.width / droppableScaleFactor
        const pageHeight = over.rect.height / droppableScaleFactor
        const tableWidth = pageWidth * STATIC_TABLE_WIDTH_RATIO
        const tableHeight =
          STATIC_TABLE_TITLE_HEIGHT +
          (STATIC_TABLE_HEADER_ROWS + STATIC_TABLE_DATA_ROWS) *
            STATIC_TABLE_ROW_HEIGHT

        const cursorScreenY = activeRect.top + activeRect.height / 2
        const cursorPdfY = (cursorScreenY - over.rect.top) / droppableScaleFactor

        const x = (pageWidth - tableWidth) / 2
        const y = Math.max(
          0,
          Math.min(cursorPdfY - tableHeight / 2, pageHeight - tableHeight)
        )

        const payload = createTableData({
          pageId: page.id,
          pageNumber: page.page_number,
          rootLayout,
          pageWidth,
          pageHeight,
          entryType,
          position: { x, y },
          // TODO: Add sectionPosition. Leaving this out for now as positioning is kind of weird and needs to be thought through better for both normal and table screens
          // sectionPosition,
        })
        dispatch(createTable({ ...payload, page }))
      } else if (['dataInput', 'pageElement'].includes(type)) {
        const { element } = active.data.current
        const {
          pageDefaultPosition,
          pageNumber,
          setGuidelineX,
          setGuidelineY,
          guidelineX,
          guidelineY
        } = over.data.current

        const { left: draggableX, top: draggableY } =
          dragOverlayRef.current.getBoundingClientRect()

        // Clears guidelines
        requestAnimationFrame(() => {
          setGuidelineX(null)
          setGuidelineY(null)
        })

        const guidelinePosition = {
          x: guidelineX,
          y: guidelineY,
        }

        const { onPage, ...calculatedPosition } = calculatePagePosition({
          draggablePosition: {
            ...(type === 'dataInput' ? pageDefaultPosition : element.position),
            x: draggableX,
            y: draggableY,
          },
          droppablePosition: over.rect,
          guidelinePosition,
          scaleFactor: over.data.current.scaleFactor,
        })

        if (!onPage) return

        const payloadGenerator =
          type === 'dataInput' ? handleDataInputDrop : handlePdfElementDrop
        const createOrUpdateAction =
          type === 'dataInput' ? createElement : updateElement

        const payload = payloadGenerator(
          active,
          over,
          allElements,
          calculatedPosition
        )
        dispatch(createOrUpdateAction(payload))
        if (type === 'dataInput') {
          window.requestAnimationFrame(() => {
            setTimeout(() => {
              selectActiveEntryLabel(active?.data?.current?.entryData?.type)
            }, 50)
          })
        }
      }
    } else {
      setShowDropAnimation(true)
    }
  }

  const deselectActiveSelections = e => {
    if (
      activeMode == PDF_MODE &&
      e.target.classList.contains('react-transform-wrapper')
    ) {
      e.stopPropagation()
      dispatch(resetActiveEntryAndSheet())
    }
  }

  const getActiveModal = activeModalName => {
    switch (activeModalName) {
      case 'publish':
        return usersAndGroupsLoaded ? PublishModal : null
      case 'plgPurchase':
        return PurchaseModal
      default:
        return
    }
  }

  const ActiveModal = getActiveModal(activeModalName, usersAndGroupsLoaded)

  return (
    <TransformWrapperProvider transformWrapperRef={transformWrapperRef}>
      <DndContext
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <div
          className="studio-layout--workspace-container"
          onClick={deselectActiveSelections}
        >
          <Alert />
          <ActiveWorkspace />
          {aiLoadingActive && <AIFormLoader />}
          {ActiveModal && <ActiveModal />}
        </div>
        <div className="studio-layout">
          <Header />
          <StudioControls />
        </div>
        <DragOverlay dropAnimation={{ duration: showDropAnimation ? 250 : 0 }}>
          <ElementOverlay
            activeDraggable={activeDraggable}
            positionDefault={globalPositionDefault}
            stylesDefault={globalStylesDefaults}
            scaleFactor={scaleFactor}
            ref={dragOverlayRef}
          />
        </DragOverlay>
      </DndContext>
    </TransformWrapperProvider>
  )
}
